import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AgentInvocationAuditTable from './AgentInvocationAuditTable';

type Props = {
  agent?: any;
  promptVersions?: any[];
  capabilityScopes?: any[];
  contextProviders?: any[];
  routingRules?: any[];
  endpointScopes?: any[];
  a2aConfig?: any | null;
  audits?: any[];
  onRefresh: () => void;
  onEdit?: (agent: any) => void;
};

const FieldGrid: React.FC<{ items: Array<{ label: string; value: any }> }> = ({
  items,
}) => (
  <Grid container spacing={1.25}>
    {items.map((item) => (
      <React.Fragment key={item.label}>
        <Grid item xs={5}>
          <Typography variant="caption" color="text.secondary">
            {item.label}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
            {item.value === null || item.value === undefined || item.value === ''
              ? '-'
              : String(item.value)}
          </Typography>
        </Grid>
      </React.Fragment>
    ))}
  </Grid>
);

const SectionPaper: React.FC<{
  title: string;
  count?: number;
  children: React.ReactNode;
}> = ({ title, count, children }) => (
  <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      {typeof count === 'number' ? (
        <Chip size="small" variant="outlined" label={count} />
      ) : null}
    </Stack>
    {children}
  </Paper>
);

const AgentDetailPanel: React.FC<Props> = ({
                                             agent,
                                             promptVersions = [],
                                             capabilityScopes = [],
                                             contextProviders = [],
                                             routingRules = [],
                                             endpointScopes = [],
                                             a2aConfig,
                                             audits = [],
                                             onRefresh,
                                             onEdit,
                                           }) => {
  if (!agent) {
    return (
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Select an agent to inspect its prompts, capability scopes, context
          providers, routing rules, and A2A configuration.
        </Typography>
      </Paper>
    );
  }

  const activePrompt =
    promptVersions.find((p) => p.is_active) ||
    [...promptVersions].sort(
      (a, b) => (b.version_number || 0) - (a.version_number || 0)
    )[0] ||
    null;

  return (
    <Paper elevation={1} sx={{ p: 2.5 }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h5" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
              {agent.name}
            </Typography>

            <Chip size="small" variant="outlined" label={agent.slug} />

            <Chip
              size="small"
              color={agent.is_active ? 'success' : 'default'}
              variant={agent.is_active ? 'filled' : 'outlined'}
              label={agent.is_active ? 'active' : 'inactive'}
            />

            {agent.can_orchestrate ? (
              <Chip size="small" color="primary" label="orchestrator" />
            ) : null}
          </Stack>

          {agent.description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, wordBreak: 'break-word' }}>
              {agent.description}
            </Typography>
          ) : null}
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh}>
            Refresh
          </Button>

          {onEdit ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => onEdit(agent)}
            >
              Edit
            </Button>
          ) : null}
        </Stack>
      </Stack>

      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} lg={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Definition
            </Typography>

            <FieldGrid
              items={[
                { label: 'Default Model', value: agent.default_model },
                { label: 'Default Temp', value: agent.default_temperature },
                { label: 'Orchestrator', value: String(!!agent.can_orchestrate) },
                { label: 'Active', value: String(!!agent.is_active) },
                { label: 'Updated By', value: agent.updated_by },
                { label: 'Last Updated', value: agent.last_updated },
              ]}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              A2A
            </Typography>

            {a2aConfig ? (
              <FieldGrid
                items={[
                  { label: 'Auth Mode', value: a2aConfig.auth_mode },
                  { label: 'Inbound', value: String(!!a2aConfig.inbound_enabled) },
                  { label: 'Outbound', value: String(!!a2aConfig.outbound_enabled) },
                  {
                    label: 'External Out',
                    value: String(!!a2aConfig.allow_external_outbound),
                  },
                  {
                    label: 'External In',
                    value: String(!!a2aConfig.allow_external_inbound),
                  },
                ]}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                A2A not configured for this agent.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <SectionPaper title="Active Prompt" count={promptVersions.length}>
        {activePrompt ? (
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <Chip size="small" label={`v${activePrompt.version_number}`} />
              {activePrompt.is_active ? (
                <Chip size="small" color="success" label="active" />
              ) : null}
              {activePrompt.change_summary ? (
                <Typography variant="caption" color="text.secondary">
                  {activePrompt.change_summary}
                </Typography>
              ) : null}
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                maxHeight: 240,
                overflow: 'auto',
                bgcolor: 'action.hover',
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}
              >
                {activePrompt.prompt_text}
              </Typography>
            </Paper>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No prompt versions defined.
          </Typography>
        )}
      </SectionPaper>

      <SectionPaper title="Capability Scopes" count={capabilityScopes.length}>
        {capabilityScopes.length ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {capabilityScopes.map((scope, idx) => (
              <Chip
                key={String(scope.id || idx)}
                size="small"
                color={scope.allow === false ? 'error' : 'success'}
                variant="outlined"
                label={`${scope.allow === false ? 'deny' : 'allow'}: ${scope.capability_tag}`}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No capability scopes (no ceiling — inherits all capabilities).
          </Typography>
        )}
      </SectionPaper>

      <SectionPaper title="Context Providers" count={contextProviders.length}>
        {contextProviders.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Entity Type</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Required</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contextProviders.map((cp, idx) => (
                <TableRow key={String(cp.id || idx)}>
                  <TableCell>
                    {cp.entity_type_label || cp.entity_type_key || cp.entity_type}
                  </TableCell>
                  <TableCell>{cp.priority ?? 0}</TableCell>
                  <TableCell>{String(!!cp.is_required)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No context providers configured.
          </Typography>
        )}
      </SectionPaper>

      {agent.can_orchestrate ? (
        <SectionPaper title="Routing Rules (outgoing)" count={routingRules.length}>
          {routingRules.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Priority</TableCell>
                  <TableCell>Match</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Permission</TableCell>
                  <TableCell>Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...routingRules]
                  .sort((a, b) => (a.priority || 0) - (b.priority || 0))
                  .map((rule, idx) => (
                    <TableRow key={String(rule.id || idx)}>
                      <TableCell>{rule.priority ?? 0}</TableCell>
                      <TableCell>
                        {rule.match_type}
                        {rule.match_value ? `: ${rule.match_value}` : ''}
                      </TableCell>
                      <TableCell>
                        {rule.target_type === 'a2a_endpoint'
                          ? `endpoint: ${rule.target_endpoint_slug || rule.target_endpoint || '-'}`
                          : `agent: ${rule.target_agent_slug || rule.target_agent || '-'}`}
                      </TableCell>
                      <TableCell>{rule.required_permission || '-'}</TableCell>
                      <TableCell>{String(!!rule.is_active)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No routing rules defined.
            </Typography>
          )}
        </SectionPaper>
      ) : null}

      <SectionPaper title="A2A Endpoint Scopes" count={endpointScopes.length}>
        {endpointScopes.length ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {endpointScopes.map((scope, idx) => (
              <Chip
                key={String(scope.id || idx)}
                size="small"
                color={scope.allow === false ? 'error' : 'success'}
                variant="outlined"
                label={`${scope.allow === false ? 'deny' : 'allow'}: ${scope.endpoint_slug || scope.endpoint_name || scope.endpoint}`}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No endpoint scopes configured.
          </Typography>
        )}
      </SectionPaper>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight={700} mb={1}>
        Invocation Audit
      </Typography>

      <AgentInvocationAuditTable audits={audits} />
    </Paper>
  );
};

export default AgentDetailPanel;
